import React from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import TrodoLoader from 'shared/components/ui/TrodoLoader';

const MESSAGES = [
  'Syncing workspace',
  'Loading team',
  'Fetching insights',
  'Hydrating agents',
  'Almost ready',
];

/**
 * Full-bleed loading state shown during post-auth bootstrap (preload, subscription, redirect)
 * and during team switches. The triangle loader on a plain surface.
 * Themed via CSS vars — works in light and dark.
 */
function WorkspaceSetupOverlay() {
  const reduceMotion = useReducedMotion();
  const [msgIdx, setMsgIdx] = React.useState(0);

  React.useEffect(() => {
    if (reduceMotion) return undefined;
    const id = setInterval(() => setMsgIdx((i) => (i + 1) % MESSAGES.length), 1800);
    return () => clearInterval(id);
  }, [reduceMotion]);

  return (
    <motion.div
      role="status"
      aria-live="polite"
      aria-busy="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.28 }}
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center overflow-hidden"
      style={{ background: 'var(--surface-base)' }}
    >
      <span className="sr-only">Loading. Setting up your workspace.</span>

      {/* Soft radial vignette to focus the center: tinted dark falloff so it reads on both themes */}
      <div aria-hidden className="workspace-vignette absolute inset-0 pointer-events-none" />

      {/* Centerpiece */}
      <div className="relative z-10 flex flex-col items-center">
        <TrodoLoader size={144} label={null} />

        <div className="mt-10 h-6 relative w-[260px] text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={msgIdx}
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
              transition={{ duration: reduceMotion ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 text-[13px] font-medium tracking-wide uppercase"
              style={{
                color: 'var(--text-muted)',
                letterSpacing: '0.14em',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {MESSAGES[msgIdx]}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom edge progress sliver: full width */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden"
        style={{ background: 'var(--border-default)' }}
      >
        <div
          className="workspace-setup-progress-bar absolute inset-y-0 left-0 w-[28%]"
          style={{ background: 'var(--trodo-primary)' }}
        />
      </div>

      <style>{`
        /* Light: bright halo at center → transparent. Reads as a glowing disc against the warm base. */
        .workspace-vignette {
          background:
            radial-gradient(circle at 50% 50%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 18%, rgba(255,255,255,0) 42%),
            radial-gradient(circle at 50% 50%, transparent 50%, rgba(26,26,25,0.06) 80%, rgba(26,26,25,0.14) 100%);
        }
        /* Dark: dark falloff at edges → spotlight effect. */
        :root[data-theme="dark"] .workspace-vignette {
          background: radial-gradient(65% 55% at 50% 50%, transparent 0%, rgba(0, 0, 0, 0.45) 75%, rgba(0, 0, 0, 0.75) 100%);
        }
        .workspace-setup-progress-bar {
          animation: workspaceSetupIndeterminate 1.85s cubic-bezier(0.45, 0, 0.2, 1) infinite;
        }
        @keyframes workspaceSetupIndeterminate {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(420%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .workspace-setup-progress-bar {
            animation: none;
            width: 60%;
            left: 20%;
            transform: none;
          }
        }
      `}</style>
    </motion.div>
  );
}

export default WorkspaceSetupOverlay;
